import React, { useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import PageNavigator from "../../../components/pagination/PageNavigator";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import NoTaxIcon from "../../../../assets/img/company/NoTaxIcon.svg";
import { TaxesEditForm } from "../company-edit-Dialog/taxes/TaxesEditForm";
import { deleteTax, fetchTaxes } from "../../../../_redux/taxes/taxesActions";
import DeleteIcon from "../../../../assets/img/company/Vector.svg";
import { i18n } from "../../private/languageSelector";
import SubscriptionDialog from "../company-edit-Dialog/SubscriptionDialog";
import CustomSearch from "../../../components/searchBar/CustomSearch";
import { sortCaret } from "../../../../_metronic/_helpers";
import { getAllProducts } from "../../../../_redux/products/productsCrud";
import { disabledSelectRowSetting, handleDataPermissions } from "../../../../helpers/helpers";

const Taxes = () => {
  const dispatch = useDispatch();
  const { language } = useSelector((state) => state.auth);

  const { taxes, user } = useSelector(
    (state) => ({
      taxes: state.taxes?.entities,
      user: state.auth?.user,
    }),
    shallowEqual
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openTaxesAddDialog, setOpenTaxesAddDialog] = useState(false);
  const [edit, setEdit] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [showDeleteTaxModal, setShowDeleteTaxModal] = useState(false);
  const [sortBy, setSortBy] = useState("desc");
  const [sortAt, setSortAt] = useState("created_at");
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getAllProducts().then((res) => {
      setProducts(res?.data?.products);
    });
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
  };

  const handleEditTaxClick = (row) => {
    if (handleDataPermissions(user)?.editData) {
      setEdit(true);
      setSelectedTax(row);
      setOpenTaxesAddDialog(true);
    }
  };

  let filteredTaxes = taxes?.filter((tax) => {
    let searchText = searchValue?.toLowerCase();
    return tax?.name?.toLowerCase().includes(searchText);
  });

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, index) => (part.toLowerCase() === query.toLowerCase() ? <b key={index}>{part}</b> : part));
  };

  useEffect(() => {
    dispatch(fetchTaxes(limit, page, sortBy, sortAt));
  }, [dispatch, limit, page, sortBy, sortAt]);

  const columns = [
    {
      dataField: "name",
      text: i18n("Customer.NAME"),
      headerAlign: "start",
      headerStyle: {
        textAlign: "left",
        fontWeight: "400",
        fontSize: "14px",
        color: "#828282",
      },
      align: language === "arabic" ? "start" : "left",
      style: {
        paddingTop: "15px",
        paddingBottom: "15px",
      },
      formatter: (cell) => highlightMatch(cell, searchValue),
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "type",
      text: i18n("global.Scheme"),
      align: language === "arabic" ? "start" : "left",
      headerStyle: {
        textAlign: "start",
        fontWeight: "400",
        fontSize: "14px",
        color: "#828282",
      },
      style: {
        paddingTop: "15px",
        paddingBottom: "15px",
      },
      formatter: (cell) => (cell?.includes("Added") ? i18n("global.AddedToPrice") : i18n("global.IncludedInPrice")),
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "taxRate",
      text: i18n("global.Rate"),
      align: language === "arabic" ? "left" : "right",
      headerStyle: {
        textAlign: language === "arabic" ? "left" : "right",
        fontWeight: "400",
        fontSize: "14px",
        color: "#828282",
      },
      style: {
        paddingTop: "15px",
        paddingBottom: "15px",
      },
      formatter: (cell) => {
        const formattedCell = `${cell}%`;
        return highlightMatch(formattedCell, searchValue);
      },
      sort: true,
      sortCaret: sortCaret,
    },
  ];

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    onSelect: (row, isSelected) => {
      const newSelectedRows = isSelected ? [...selectedRows, row._id] : selectedRows.filter((id) => id !== row._id);
      setSelectedRows(newSelectedRows);
      return true;
    },
    onSelectAll: (isSelected, rows) => {
      const newSelectedRows = isSelected ? rows.map((row) => row._id) : [];
      setSelectedRows(newSelectedRows);
    },
  };

  const handleSearchInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  if (!taxes) {
    return null;
  }

  const confirmDelete = () => {
    if (selectedRows.length > 0 && !openTaxesAddDialog) {
      for (let row of selectedRows) {
        dispatch(deleteTax(row));
      }
      setSelectedRows([]);
      setShowDeleteTaxModal(false);
    } else if (selectedTax?._id) {
      dispatch(deleteTax(selectedTax._id));
      setSelectedRows([]);
      setShowDeleteTaxModal(false);
      setOpenTaxesAddDialog(false);
    }
  };

  const handleSorting = (type, sortOrder, sortField, data, page, sizePerPage) => {
    if (type === "sort") {
      if (sortOrder === "asc") {
        setSortBy("asc");
      } else {
        setSortBy("desc");
      }
      setSortAt(sortField);
    }
  };

  return (
    <>
      <div className="table-width" style={{ backgroundColor: "#FFFFFF", borderRadius: "0.42rem", float: language === "arabic" ? "right" : "left" }}>
        <div className={`pl-4 pr-4 py-4 mb-3 d-flex justify-content-between align-items-center ${language === "arabic" ? "rtl" : "ltr"}`}>
          <CustomSearch
            handleSearchInputChange={handleSearchInputChange}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setShowSearchInput={setShowSearchInput}
            showSearchInput={showSearchInput}
          />
          <div>
            {selectedRows.length > 0 && handleDataPermissions(user)?.deleteData && (
              <span className={` cursor-pointer ${language === "arabic" ? "ml-5" : "mr-5"}`} onClick={() => setShowDeleteTaxModal(true)}>
                <img src={DeleteIcon} alt="Delete" />
              </span>
            )}
            {handleDataPermissions(user)?.createData ? (
              <span
                className="add-text-btn"
                onClick={() => {
                  setEdit(false);
                  setSelectedTax(null);
                  setOpenTaxesAddDialog(true);
                }}
              >
                {i18n("global.AddTax")}
              </span>
            ) : null}
          </div>
        </div>
        <BootstrapTable
          wrapperClasses="table-responsive"
          classes={`table table-head-custom table-vertical-center overflow-hidden ${language === "arabic" ? "rtl" : "ltr"}`}
          remote
          bordered={false}
          bootstrap4
          keyField="_id"
          columns={columns}
          data={filteredTaxes}
          selectRow={handleDataPermissions(user)?.deleteData ? selectRow : disabledSelectRowSetting}
          rowClasses="cursor-pointer"
          onTableChange={(type, { page, sizePerPage, sortField, sortOrder, data }) =>
            handleSorting(type, sortOrder, sortField, data, page, sizePerPage)
          }
          rowEvents={{
            onClick: (e, row, rowIndex) => {
              if (e.target.type !== "checkbox") {
                handleEditTaxClick(row);
              }
            },
          }}
        />

        {taxes && taxes?.length < 1 && (
          <div className="d-flex flex-column justify-content-center align-items-center mt-5 mb-5">
            <img src={NoTaxIcon} alt="NoTaxIcon" />
            <p className="py-3 you-have-no-taxes">{i18n("global.YouHaveNoTaxes")}</p>
          </div>
        )}

        <div className="pb-3 d-flex justify-content-end mr-3 ml-3">
          <PageNavigator page={page} limit={limit} handlePageChange={handlePageChange} handleLimitChange={handleLimitChange} data={filteredTaxes} />
        </div>
      </div>
      <TaxesEditForm
        openTaxesAddDialog={openTaxesAddDialog}
        setOpenTaxesAddDialog={setOpenTaxesAddDialog}
        edit={edit}
        tax={selectedTax}
        setShowDeleteTaxModal={setShowDeleteTaxModal}
        products={products}
      />

      {/* delete tax modal  */}
      <SubscriptionDialog
        show={showDeleteTaxModal}
        onHide={() => setShowDeleteTaxModal(false)}
        modalWidth="350px"
        modalHeight="185px"
        message={selectedRows.length === 1 || edit ? i18n("global.AreYouSureToDeleteTax") : i18n("global.AreYouSureToDeleteTaxes")}
        messageWidth="320px"
        messageHeight="44px"
        messageColor="#333333"
        messageFontWeight="400"
        messageFontSize="16px"
        messageLineHeight="21.79px"
        primaryAction={() => setShowDeleteTaxModal(false)}
        primaryLabel={i18n("SubscriptionModals.CANCEL")}
        primaryLabelWidth="55px"
        primaryLabelHeight="20px"
        primaryLabelFontWeight="700"
        primaryLabelFontSize="14px"
        primaryLabelLineHeight="20px"
        primaryLabelColor="#828282"
        secondaryAction={confirmDelete}
        secondaryLabel={i18n("Discount.DELETE")}
        secondaryLabelWidth="67px"
        secondaryLabelHeight="20px"
        secondaryLabelFontWeight="700"
        secondaryLabelFontSize="14px"
        secondaryLabelLineHeight="20px"
        secondaryLabelColor="#EB5757"
        isCenteredOnMobile
      />
    </>
  );
};

export default Taxes;
